import React from 'react';
import styled from '@benzinga/themetron';
import type { ColumnDef } from '../../interface';
import type { SortBy } from '../../libs/utils';
import { Tooltip } from '@benzinga/core-ui';

export interface TableHeadProps {
  columnsDef: ColumnDef[];
  onSortChange?: (newSortByField: string, columnDef: ColumnDef) => void;
  hiddenColumnsWhenGated?: Set<string>;
  sortBy?: SortBy;
  style?: React.CSSProperties;
  isGated?: boolean;
  $alignLastRowsRight?: boolean;
  $hasStockTableStyling?: boolean;
}

export const TableHead: React.FC<TableHeadProps> = ({
  columnsDef,
  hiddenColumnsWhenGated,
  isGated,
  onSortChange,
  sortBy,
  style,
  ...props
}) => {
  return (
    <THeadStyled {...props} className="benzinga-core-table-thead whitespace-nowrap" style={style}>
      <tr>
        {columnsDef &&
          columnsDef.map((column, index) => {
            if (isGated && hiddenColumnsWhenGated?.has(column.field)) return null;
            const defaultHeaderLabel = column.headerName ?? (column.field ? column.field : '');
            const thContent = column.headerRenderer ? (
              column.headerRenderer()
            ) : sortBy && column.field && column.sortEnabled && onSortChange ? (
              <button
                className="inline-flex items-center gap-1 font-semibold leading-none"
                onClick={() => onSortChange(column.field, column)}
                type="button"
              >
                <span className="normal-case">{defaultHeaderLabel}</span>
                {sortBy && <HeaderSortIcons field={column.field} sortBy={sortBy} />}
              </button>
            ) : (
              defaultHeaderLabel
            );
            return (
              <th
                aria-hidden={column.ariaHidden}
                className={`benzinga-core-table-th normal-case font-semibold ${column.headerClass || ''}`}
                colSpan={column.colSpan ? column.colSpan : 1}
                key={`table-head-${column.field}-${index}`}
              >
                {column.headerTooltip ? (
                  <Tooltip content={column.headerTooltip} width={160}>
                    {thContent}
                  </Tooltip>
                ) : (
                  thContent
                )}
              </th>
            );
          })}
      </tr>
    </THeadStyled>
  );
};

export const HeaderSortIcons: React.FC<{ field: string; sortBy: SortBy }> = ({ field, sortBy }) => {
  const active = field === sortBy.field;
  return (
    <span
      aria-hidden
      aria-label={`Sort by field: ${field}, in ${sortBy.direction} order`}
      className={`text-xs ${active ? 'text-gray-600' : 'text-gray-300'}`}
    >
      {active ? (sortBy.direction === 'asc' ? '▲' : '▼') : '⇅'}
    </span>
  );
};

export const THeadStyled = styled.thead<{ $alignLastRowsRight?: boolean; $hasStockTableStyling?: boolean }>`
  width: 100%;
  font-size: 0.725rem;
  font-weight: 600;
  min-height: 35px;
  height: 35px;

  tr {
    text-align: left;
    border-collapse: collapse;
    color: ${({ $hasStockTableStyling, theme }) =>
      $hasStockTableStyling ? theme.colorPalette.white : theme.colorPalette.white};
    background-color: ${({ $hasStockTableStyling, theme }) =>
      $hasStockTableStyling ? '#072232' : theme.colorPalette.blue500};
    border-bottom: ${({ $hasStockTableStyling, theme }) =>
      $hasStockTableStyling ? '3px solid #2ca2d1' : `2px solid ${theme.colors.border}`};
    position: sticky;
    top: 0;
    z-index: 1;

    th {
      border-left: ${({ $hasStockTableStyling }) => ($hasStockTableStyling ? '1px solid #aaa' : 'none')};
      &:first-child {
        border-left: ${({ $hasStockTableStyling, theme }) =>
          $hasStockTableStyling ? '1px solid #072232' : `1px solid ${theme.colorPalette.blue500}`};
      }
      &:last-of-type {
        text-align: ${({ $alignLastRowsRight }) => ($alignLastRowsRight ? 'right' : 'unset')};
        border-right: ${({ $hasStockTableStyling, theme }) =>
          $hasStockTableStyling ? '1px solid #072232' : `1px solid ${theme.colorPalette.blue500}`};
      }
    }
  }

  th,
  td {
    position: sticky;
    top: 0;
    z-index: 1;
  }
`;
